import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, SearchIcon, FilterIcon, ClockIcon, CheckCircleIcon, XCircleIcon, FileTextIcon, UploadIcon, CalendarIcon, CreditCardIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeReason } from '../types/Dispute';
export function Disputes() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DisputeStatus | 'all'>('all');
  const [reasonFilter, setReasonFilter] = useState<DisputeReason | 'all'>('all');
  // Mock data
  const disputes: Dispute[] = [{
    id: '1',
    disputeId: 'DP-2024-001',
    transactionId: 'TXN-2024-1234',
    amount: 125000,
    currency: 'LKR',
    status: 'evidence_required',
    reason: 'fraudulent',
    reasonDescription: 'Customer claims transaction was unauthorized',
    createdAt: '2024-01-15T10:30:00Z',
    dueDate: '2024-01-29T23:59:59Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    cardBrand: 'Visa',
    cardLast4: '4242',
    networkReasonCode: '10.4',
    isChargeback: true,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T10:30:00Z',
      status: 'new',
      description: 'Dispute opened by cardholder'
    }, {
      id: '2',
      timestamp: '2024-01-15T11:00:00Z',
      status: 'evidence_required',
      description: 'Evidence submission required'
    }]
  }, {
    id: '2',
    disputeId: 'DP-2024-002',
    transactionId: 'TXN-2024-1567',
    amount: 87500,
    currency: 'LKR',
    status: 'under_review',
    reason: 'product_not_received',
    reasonDescription: 'Customer claims product was not delivered',
    createdAt: '2024-01-10T14:20:00Z',
    dueDate: '2024-01-24T23:59:59Z',
    respondedAt: '2024-01-12T09:15:00Z',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.j@email.com',
    cardBrand: 'Mastercard',
    cardLast4: '5555',
    networkReasonCode: '13.1',
    isChargeback: false,
    evidence: {
      shippingDocumentation: 'Tracking number: TRK123456789',
      documents: [{
        id: '1',
        name: 'shipping-receipt.pdf',
        type: 'application/pdf',
        size: 245000,
        uploadedAt: '2024-01-12T09:15:00Z',
        url: '#'
      }],
      submittedAt: '2024-01-12T09:15:00Z'
    },
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-10T14:20:00Z',
      status: 'new',
      description: 'Dispute opened by cardholder'
    }, {
      id: '2',
      timestamp: '2024-01-10T14:30:00Z',
      status: 'evidence_required',
      description: 'Evidence submission required'
    }, {
      id: '3',
      timestamp: '2024-01-12T09:15:00Z',
      status: 'evidence_submitted',
      description: 'Evidence submitted by merchant',
      actor: 'merchant'
    }, {
      id: '4',
      timestamp: '2024-01-12T09:20:00Z',
      status: 'under_review',
      description: 'Under review by card network'
    }]
  }, {
    id: '3',
    disputeId: 'DP-2024-003',
    transactionId: 'TXN-2024-0892',
    amount: 45000,
    currency: 'LKR',
    status: 'won',
    reason: 'duplicate',
    reasonDescription: 'Customer claims duplicate charge',
    createdAt: '2024-01-05T09:00:00Z',
    dueDate: '2024-01-19T23:59:59Z',
    respondedAt: '2024-01-06T10:30:00Z',
    resolvedAt: '2024-01-14T16:45:00Z',
    customerName: 'Michael Brown',
    customerEmail: 'mbrown@email.com',
    cardBrand: 'Visa',
    cardLast4: '1234',
    networkReasonCode: '12.6',
    isChargeback: false,
    evidence: {
      duplicateChargeDocumentation: 'Proof that charges were for different services',
      documents: [{
        id: '1',
        name: 'invoice-comparison.pdf',
        type: 'application/pdf',
        size: 189000,
        uploadedAt: '2024-01-06T10:30:00Z',
        url: '#'
      }],
      submittedAt: '2024-01-06T10:30:00Z'
    },
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-05T09:00:00Z',
      status: 'new',
      description: 'Dispute opened by cardholder'
    }, {
      id: '2',
      timestamp: '2024-01-05T09:15:00Z',
      status: 'evidence_required',
      description: 'Evidence submission required'
    }, {
      id: '3',
      timestamp: '2024-01-06T10:30:00Z',
      status: 'evidence_submitted',
      description: 'Evidence submitted by merchant',
      actor: 'merchant'
    }, {
      id: '4',
      timestamp: '2024-01-06T10:45:00Z',
      status: 'under_review',
      description: 'Under review by card network'
    }, {
      id: '5',
      timestamp: '2024-01-14T16:45:00Z',
      status: 'won',
      description: 'Dispute resolved in favor of merchant'
    }]
  }, {
    id: '4',
    disputeId: 'DP-2024-004',
    transactionId: 'TXN-2024-0654',
    amount: 165000,
    currency: 'LKR',
    status: 'lost',
    reason: 'product_unacceptable',
    reasonDescription: 'Customer claims product quality was unacceptable',
    createdAt: '2024-01-03T11:20:00Z',
    dueDate: '2024-01-17T23:59:59Z',
    respondedAt: '2024-01-04T14:00:00Z',
    resolvedAt: '2024-01-13T10:30:00Z',
    customerName: 'Emily Davis',
    customerEmail: 'emily.d@email.com',
    cardBrand: 'Mastercard',
    cardLast4: '8888',
    networkReasonCode: '13.3',
    isChargeback: true,
    evidence: {
      customerCommunication: 'Email exchange with customer',
      refundPolicy: 'Standard 30-day return policy',
      documents: [{
        id: '1',
        name: 'email-correspondence.pdf',
        type: 'application/pdf',
        size: 312000,
        uploadedAt: '2024-01-04T14:00:00Z',
        url: '#'
      }],
      submittedAt: '2024-01-04T14:00:00Z'
    },
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-03T11:20:00Z',
      status: 'new',
      description: 'Dispute opened by cardholder'
    }, {
      id: '2',
      timestamp: '2024-01-03T11:30:00Z',
      status: 'evidence_required',
      description: 'Evidence submission required'
    }, {
      id: '3',
      timestamp: '2024-01-04T14:00:00Z',
      status: 'evidence_submitted',
      description: 'Evidence submitted by merchant',
      actor: 'merchant'
    }, {
      id: '4',
      timestamp: '2024-01-04T14:15:00Z',
      status: 'under_review',
      description: 'Under review by card network'
    }, {
      id: '5',
      timestamp: '2024-01-13T10:30:00Z',
      status: 'lost',
      description: 'Dispute resolved in favor of cardholder'
    }]
  }, {
    id: '5',
    disputeId: 'DP-2024-005',
    transactionId: 'TXN-2024-2341',
    amount: 95000,
    currency: 'LKR',
    status: 'new',
    reason: 'unrecognized',
    reasonDescription: 'Customer does not recognize the transaction',
    createdAt: '2024-01-18T15:45:00Z',
    dueDate: '2024-02-01T23:59:59Z',
    customerName: 'David Wilson',
    customerEmail: 'dwilson@email.com',
    cardBrand: 'Visa',
    cardLast4: '6789',
    networkReasonCode: '10.1',
    isChargeback: false,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T15:45:00Z',
      status: 'new',
      description: 'Dispute opened by cardholder'
    }]
  }];
  // Calculate statistics
  const totalDisputes = disputes.length;
  const activeDisputes = disputes.filter(d => ['new', 'evidence_required', 'evidence_submitted', 'under_review'].includes(d.status)).length;
  const wonDisputes = disputes.filter(d => d.status === 'won').length;
  const lostDisputes = disputes.filter(d => d.status === 'lost').length;
  const winRate = wonDisputes + lostDisputes > 0 ? Math.round(wonDisputes / (wonDisputes + lostDisputes) * 100) : 0;
  // Filter disputes
  const filteredDisputes = disputes.filter(dispute => {
    const matchesSearch = dispute.disputeId.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.customerName.toLowerCase().includes(searchQuery.toLowerCase()) || dispute.customerEmail.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || dispute.status === statusFilter;
    const matchesReason = reasonFilter === 'all' || dispute.reason === reasonFilter;
    return matchesSearch && matchesStatus && matchesReason;
  });
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      evidence_required: 'bg-yellow-100 text-yellow-800',
      evidence_submitted: 'bg-purple-100 text-purple-800',
      under_review: 'bg-orange-100 text-orange-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800',
      closed: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      evidence_required: 'Evidence Required',
      evidence_submitted: 'Evidence Submitted',
      under_review: 'Under Review',
      won: 'Won',
      lost: 'Lost',
      closed: 'Closed',
      expired: 'Expired'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-4191">
        {labels[status]}
      </span>;
  };
  const getReasonLabel = (reason: DisputeReason) => {
    const labels = {
      fraudulent: 'Fraudulent',
      unrecognized: 'Unrecognized',
      duplicate: 'Duplicate Charge',
      product_not_received: 'Product Not Received',
      product_unacceptable: 'Product Unacceptable',
      credit_not_processed: 'Credit Not Processed',
      general: 'General'
    };
    return labels[reason];
  };
  const getDaysRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  return <div className="space-y-6" data-id="element-4192">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4193">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-4194">
          <div className="flex items-center justify-between" data-id="element-4195">
            <div data-id="element-4196">
              <div className="text-sm font-medium text-gray-500" data-id="element-4197">
                Total Disputes
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-4198">
                {totalDisputes}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-4199">
              <AlertTriangleIcon size={20} className="text-blue-600" data-id="element-4200" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-4201">
          <div className="flex items-center justify-between" data-id="element-4202">
            <div data-id="element-4203">
              <div className="text-sm font-medium text-gray-500" data-id="element-4204">
                Active Disputes
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-4205">
                {activeDisputes}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-4206">
              <ClockIcon size={20} className="text-yellow-600" data-id="element-4207" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-4208">
          <div className="flex items-center justify-between" data-id="element-4209">
            <div data-id="element-4210">
              <div className="text-sm font-medium text-gray-500" data-id="element-4211">Win Rate</div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-4212">
                {winRate}%
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-4213">
              <TrendingUpIcon size={20} className="text-green-600" data-id="element-4214" />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-4215">
          <div className="flex items-center justify-between" data-id="element-4216">
            <div data-id="element-4217">
              <div className="text-sm font-medium text-gray-500" data-id="element-4218">
                Lost Disputes
              </div>
              <div className="text-2xl font-semibold text-gray-900 mt-1" data-id="element-4219">
                {lostDisputes}
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4220">
              <TrendingDownIcon size={20} className="text-red-600" data-id="element-4221" />
            </div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200" data-id="element-4222">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-4223">
          <div className="relative flex-grow" data-id="element-4224">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4225">
              <SearchIcon size={16} className="text-gray-400" data-id="element-4226" />
            </div>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by dispute ID, transaction ID, or customer..." data-id="element-4227" />
          </div>
          <div className="flex gap-2" data-id="element-4228">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as DisputeStatus | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-4229">
              <option value="all" data-id="element-4230">All Statuses</option>
              <option value="new" data-id="element-4231">New</option>
              <option value="evidence_required" data-id="element-4232">Evidence Required</option>
              <option value="evidence_submitted" data-id="element-4233">Evidence Submitted</option>
              <option value="under_review" data-id="element-4234">Under Review</option>
              <option value="won" data-id="element-4235">Won</option>
              <option value="lost" data-id="element-4236">Lost</option>
              <option value="closed" data-id="element-4237">Closed</option>
              <option value="expired" data-id="element-4238">Expired</option>
            </select>
            <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value as DisputeReason | 'all')} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-4239">
              <option value="all" data-id="element-4240">All Reasons</option>
              <option value="fraudulent" data-id="element-4241">Fraudulent</option>
              <option value="unrecognized" data-id="element-4242">Unrecognized</option>
              <option value="duplicate" data-id="element-4243">Duplicate</option>
              <option value="product_not_received" data-id="element-4244">Product Not Received</option>
              <option value="product_unacceptable" data-id="element-4245">Product Unacceptable</option>
              <option value="credit_not_processed" data-id="element-4246">Credit Not Processed</option>
              <option value="general" data-id="element-4247">General</option>
            </select>
          </div>
        </div>
        {/* Disputes List */}
        <div className="overflow-x-auto" data-id="element-4248">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4249">
            <thead className="bg-gray-50" data-id="element-4250">
              <tr data-id="element-4251">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4252">
                  Dispute
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4253">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4254">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4255">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4256">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4257">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4258">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4259">
              {filteredDisputes.map(dispute => {
              const daysRemaining = getDaysRemaining(dispute.dueDate);
              const isUrgent = daysRemaining <= 3 && daysRemaining >= 0;
              return <tr key={dispute.id} className="hover:bg-gray-50" data-id="element-4260">
                    <td className="px-6 py-4" data-id="element-4261">
                      <div data-id="element-4262">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-4263">
                          {dispute.disputeId}
                          {dispute.isChargeback && <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded" data-id="element-4264">
                              Chargeback
                            </span>}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-4265">
                          {dispute.transactionId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-4266">
                      <div data-id="element-4267">
                        <div className="text-sm font-medium text-gray-900" data-id="element-4268">
                          {dispute.customerName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-4269">
                          {dispute.customerEmail}
                        </div>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-4270">
                          {dispute.cardBrand} •••• {dispute.cardLast4}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-4271">
                      Rs. {dispute.amount.toLocaleString('en-LK')}
                    </td>
                    <td className="px-6 py-4" data-id="element-4272">
                      <div className="text-sm text-gray-900" data-id="element-4273">
                        {getReasonLabel(dispute.reason)}
                      </div>
                      {dispute.networkReasonCode && <div className="text-xs text-gray-500" data-id="element-4274">
                          Code: {dispute.networkReasonCode}
                        </div>}
                    </td>
                    <td className="px-6 py-4" data-id="element-4275">
                      {getStatusBadge(dispute.status)}
                    </td>
                    <td className="px-6 py-4" data-id="element-4276">
                      <div className="text-sm text-gray-900" data-id="element-4277">
                        {new Date(dispute.dueDate).toLocaleDateString('en-GB')}
                      </div>
                      {daysRemaining >= 0 && <div className={`text-xs ${isUrgent ? 'text-red-600 font-medium' : 'text-gray-500'}`} data-id="element-4278">
                          {daysRemaining} days remaining
                        </div>}
                    </td>
                    <td className="px-6 py-4 text-right text-sm" data-id="element-4279">
                      <button onClick={() => navigate(`/dispute-detail/${dispute.id}`)} className="text-blue-600 hover:text-blue-900 font-medium" data-id="element-4280">
                        View Details
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        {filteredDisputes.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-4281">
            <AlertTriangleIcon size={48} className="text-gray-400 mb-4" data-id="element-4282" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-4283">
              No disputes found
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-4284">
              No disputes match your current search criteria. Try adjusting your
              filters.
            </p>
          </div>}
      </div>
    </div>;
}